// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { canAddToCart } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { QtyFieldStyles } from "@rpsc/App/Components/Qty/QtyField";
import ProductQuantityOrdered from "@rpsc/Overrides/Components/ProductQuantityOrdered";
import { mergeWithCss } from "@rpsc/XDL/Utilities";
import * as React from "react";
import { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

export interface ProductActionsProps {
    carouselId?: string;
    extendedStyles?: ProductActionsStyle;
    onAddToCartClick?: () => void;
    showDisabled?: boolean;
    isPdp?: boolean;
    isProductOptionsModal?: boolean;
}

type Props = ProductActionsProps & HasProductContext & ReturnType<typeof mapStateToProps> & HasProductContext;

const mapStateToProps = (state: ApplicationState, ownProps: ProductActionsProps & HasProductContext) => {
    const configurationSelection = ownProps.isPdp ? state.pages.productDetails.configurationSelection : undefined;
    const configurationCompleted = ownProps.isPdp && state.pages.productDetails.configurationCompleted;
    const variantSelectionCompleted = ownProps.isPdp && state.pages.productDetails.variantSelectionCompleted;

    return {
        configurationSelection,
        configurationCompleted,
        variantSelectionCompleted,
        subscription: ownProps.isPdp ? state.pages.productDetails.subscription : undefined,
        canAddToCart: canAddToCart(
            state,
            ownProps.productContext.product,
            ownProps.productContext.productInfo,
            configurationCompleted,
            variantSelectionCompleted,
            configurationSelection,
        ),
        productSettings: getSettingsCollection(state).productSettings,
    };
};

export interface ProductActionsStyle {
    addToCartContainer?: GridContainerProps;
    qtyWrapper?: InjectableCss;
    qtyUomWrapper?: InjectableCss;
    productQuantityOrderedStyles?: QtyFieldStyles;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    addToCartButtonWrapper?: InjectableCss;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productUomWrapper?: InjectableCss;
}

export const productActionsStyle: ProductActionsStyle = {};

const ProductActions: FC<Props> = ({
    carouselId,
    extendedStyles,
    onAddToCartClick,
    productContext: {
        product: { unitOfMeasures },
    },
    isPdp,
    productContext,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    productSettings,
}) => {
    const [styles] = useState(() => mergeWithCss(productActionsStyle, extendedStyles));

    const hasUnitOfMeasureOrTooltip =
        (productSettings?.alternateUnitsOfMeasure &&
            unitOfMeasures &&
            unitOfMeasures.filter(uom => uom?.unitOfMeasure).length > 1) ||
        (productContext.product.minimumOrderQty && productContext.product.minimumOrderQty > 1) ||
        (productContext.product.detail?.multipleSaleQty && productContext.product.detail?.multipleSaleQty > 1);
    return (
        <StyledWrapper
            {...styles.addToCartContainer}
            data-test-selector="product_actions"
            {...{ hasUnitOfMeasureOrTooltip }}
        >
            <StyledWrapper {...styles.qtyUomWrapper}>
                <StyledWrapper {...styles.qtyWrapper}>
                    <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                        {translate("QTY")}
                    </VisuallyHidden>
                    <ProductQuantityOrdered
                        configurationSelection={configurationSelection}
                        configurationCompleted={configurationCompleted}
                        variantSelectionCompleted={variantSelectionCompleted}
                        id={productContext.product.id}
                        data-test-selector="productQty"
                    />
                </StyledWrapper>

                {unitOfMeasures && (
                    <StyledWrapper {...styles.productUomWrapper}>
                        <ProductUnitOfMeasureSelect
                            labelOverride=""
                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                            id={`${carouselId}-uom`}
                        />
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.addToCartButtonWrapper}>
                <ProductAddToCartButton
                    onAddToCartClick={onAddToCartClick}
                    extendedStyles={styles.productAddToCartButtonStyles}
                    data-test-selector="addToCartBtn"
                    configurationSelection={configurationSelection}
                    configurationCompleted={configurationCompleted}
                    variantSelectionCompleted={variantSelectionCompleted}
                    subscription={subscription}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProductContext(connect(mapStateToProps)(ProductActions));
